import { toast } from 'sonner'
import {
  useDeleteEvent,
  useEndEventLiveRoom,
  useEventLiveRoom,
} from '~/entities/event/model/use-events'
import { liveRoomNotice } from '~/features/events/lib/live-room-notice'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { DESTRUCTIVE_OUTLINE_BUTTON } from '~/shared/styles/buttons'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/shared/ui/alert-dialog'

interface DeleteEventDialogProps {
  businessId: string
  eventId: string | null
  eventTitle?: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onDeleted?: () => void
}

/**
 * Confirms deleting an event. While people are in its Live Room it says how many and offers to
 * end only the Live Room instead.
 */
export function DeleteEventDialog({
  businessId,
  eventId,
  eventTitle,
  open,
  onOpenChange,
  onDeleted,
}: DeleteEventDialogProps) {
  const liveRoom = useEventLiveRoom(businessId, eventId ?? '', open)
  const deleteEvent = useDeleteEvent(businessId)
  const endLiveRoom = useEndEventLiveRoom(businessId)
  const notice = liveRoomNotice(liveRoom.data)
  const countPending = liveRoom.isFetching && !liveRoom.data

  const handleDelete = async () => {
    if (!eventId) return
    try {
      await deleteEvent.mutateAsync(eventId)
      toast.success(m.eventDeleted(), { description: m.theEventHasBeenRemovedSuccessfully() })
      onDeleted?.()
    } catch (error) {
      toast.error(m.failedToDeleteEvent(), { description: getErrorMessageFromError(error) })
    }
  }

  const handleEndLiveRoom = async () => {
    if (!eventId) return
    try {
      await endLiveRoom.mutateAsync(eventId)
      toast.success(m.liveRoomEnded(), { description: m.liveRoomEndedDescription() })
    } catch (error) {
      toast.error(m.failedToEndLiveRoom(), { description: getErrorMessageFromError(error) })
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {eventTitle ? m.areYouSureYouWantToDeleteEvent({ eventTitle }) : m.areYouSure()}
          </AlertDialogTitle>
          <AlertDialogDescription>{m.thisActionCannotBeUndone()}</AlertDialogDescription>
          {notice && (
            <p className="text-sm font-medium text-foreground">
              {notice.kind === 'one'
                ? m.deleteEventLiveRoomOne()
                : m.deleteEventLiveRoomMany({ count: String(notice.count) })}
            </p>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{m.cancel()}</AlertDialogCancel>
          {notice && (
            <AlertDialogAction onClick={handleEndLiveRoom} disabled={endLiveRoom.isPending}>
              {m.endLiveRoomOnly()}
            </AlertDialogAction>
          )}
          <AlertDialogAction
            onClick={handleDelete}
            disabled={countPending || deleteEvent.isPending}
            className={DESTRUCTIVE_OUTLINE_BUTTON}
          >
            {m.delete()}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
